/**
 * Offline-first task store — CRUD over the `tasks`, `taskTypes` and `contacts`
 * tables introduced in database version 4.
 *
 * Reads are open; every mutation goes through the permissions engine, exactly
 * as the fleet store does. Opening a task needs `CREATE_TASK`, changing one
 * needs `EDIT_TASK` and removing one needs `DELETE_TASK` — a soldier account
 * holds none of the three, so the board is read-only for them no matter which
 * screen the request arrives from.
 *
 * Search, filtering and ordering rules are not re-implemented here: they live
 * in `src/core/task-filters.ts` and this module calls into them.
 *
 * Deletion is soft. `deleteTask()` stamps `deletedAt` and the task moves to the
 * recycle bin (סל מחזור); it is off every read in this module until it is
 * restored, and only {@link purgeTask} — by hand, or through the retention
 * sweep — removes the row and everything hanging off it.
 */
import { db } from './index';
import { deleteTaskTeams, getTaskTeams, resetAssignmentData } from './assignment-store';
import { recordTaskEvent } from './audit-store';
import { deleteTaskTimeline, logTaskTimelineEvent, resetTimelineData } from './timeline-store';
import { resetQuickDriverTasks } from './quick-task-store';
import { DEFAULT_TASK_TYPES } from '../core/mock-tasks';
import { assertPermission, PermissionDeniedError } from '../core/permissions';
import { stampCreated, stampUpdated } from '../core/audit-stamp';
import { deletedTasks, expiredTasks, isDeleted } from '../core/recycle-bin';
import { sortTasks } from '../core/task-filters';
import { assignedSoldierIdsOf, resolveTaskView, soldierTeamOf } from '../core/task-views';
import { canAddTimelineNote } from '../core/timeline-engine';
import { emitStoreChange } from '../core/store-events';
import { soldierProgressId } from '../types/execution';
import type { SoldierTaskProgress, SoldierTaskUpdate } from '../types/execution';
import {
  ACTIVE_TASK_STATUS_ID,
  COMPLETED_TASK_STATUS_ID,
  DEFAULT_TASK_PRIORITY,
  DEFAULT_TASK_STATUS_ID,
  isLegacyTaskStatus,
  normalizeTaskStatus,
} from '../types/task';
import type { ManualContact, Task, TaskStatusId, TaskType } from '../types/task';
import type { Contact } from '../types/contact';
import type { TimelineEvent, TimelineEventType } from '../types/timeline';
import type { User } from '../types/user';

const nowIso = (now: number = Date.now()): string => new Date(now).toISOString();

const newId = (prefix: string): string => `${prefix}-${crypto.randomUUID()}`;

const trimmed = (value: string | null | undefined): string => String(value ?? '').trim();

/** Optional free-text field — an empty string is stored as "not set". */
const optional = (value: string | null | undefined): string | undefined =>
  trimmed(value) || undefined;

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;

// ── Drafts ───────────────────────────────────────────────────────────

/**
 * The caller-supplied part of a task — ids, author and stamps are ours.
 *
 * The two audit names are ours as well: a form may not nominate who made the
 * change, or the indicator on the board would be a claim rather than a record.
 */
export type TaskDraft = Omit<
  Task,
  'id' | 'createdBy' | 'createdAt' | 'updatedAt' | 'createdByName' | 'updatedByName'
>;

/** What the creation form actually submits: the four fields it insists on. */
export type TaskInput = Partial<TaskDraft> &
  Pick<TaskDraft, 'title' | 'taskTypeId' | 'date' | 'startTime'>;

export type TaskTypeDraft = { name: string; description?: string };

/** A contact being filed; supplying `id` updates the existing record. */
export type ContactDraft = Omit<Contact, 'id' | 'createdAt'> & { id?: string };

// ── The two-state lifecycle ──────────────────────────────────────────

/**
 * The task as this build reads it. A row written before the lifecycle was
 * flattened still carries "ממתינה לשיבוץ" or "משובצת"; both meant "opened, not
 * finished", so both read back as בביצוע.
 *
 * Applied on the way out of every read rather than only at migration time,
 * because a row can also arrive from the cloud mirror — from a device that has
 * not been updated yet — long after the migration ran.
 */
function asCurrent(task: Task): Task {
  const statusId = normalizeTaskStatus(task.statusId);
  return statusId === task.statusId ? task : { ...task, statusId };
}

/**
 * Rewrites the retired statuses on disk, once. Read normalisation already makes
 * the board correct; this makes the stored rows agree with it, so the mirror
 * stops pushing a status this build no longer has a name for.
 */
export async function migrateTaskStatuses(): Promise<number> {
  const rows = await db.tasks.toArray();
  const stale = rows.filter((task) => isLegacyTaskStatus(task.statusId));
  if (stale.length === 0) return 0;
  await db.tasks.bulkPut(stale.map((task) => ({ ...task, statusId: ACTIVE_TASK_STATUS_ID })));
  return stale.length;
}

// ── Seeding ──────────────────────────────────────────────────────────

/** Writes the seven shipped task types on first run only. */
export async function seedTaskTypes(): Promise<void> {
  if ((await db.taskTypes.count()) === 0) {
    await db.taskTypes.bulkPut(DEFAULT_TASK_TYPES.map((type) => ({ ...type })));
  }
}

/**
 * Everything the tasks module needs before its first render — the shipped type
 * catalogue and nothing else. The board, the contacts book and the roster all
 * start empty: an installation opens on the unit's own data, never on a demo.
 *
 * The lifecycle migration rides along: it is a single indexed read on every
 * launch after the first, and it is what lets a device that has been running
 * since before the two-state board open on statuses this build can name.
 */
export async function seedTaskData(): Promise<void> {
  await seedTaskTypes();
  await migrateTaskStatuses();
}

// ── Task types ───────────────────────────────────────────────────────

/** Catalogue order: the shipped types first, then the manager-defined ones. */
export async function getTaskTypes(): Promise<TaskType[]> {
  const types = await db.taskTypes.toArray();
  return types.sort((a, b) => {
    if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
    if (a.isSystem && b.isSystem) {
      const order = DEFAULT_TASK_TYPES.map((type) => type.id);
      return order.indexOf(a.id) - order.indexOf(b.id);
    }
    return a.createdAt.localeCompare(b.createdAt);
  });
}

export async function getTaskType(taskTypeId: string): Promise<TaskType | null> {
  return (await db.taskTypes.get(taskTypeId)) ?? null;
}

/**
 * Adds a manager-defined type from the creation form. It is neither a default
 * nor a system type, so it can be removed again later.
 */
export async function createTaskType(actor: User, draft: TaskTypeDraft): Promise<TaskType> {
  assertPermission(actor, 'CREATE_TASK');
  const name = trimmed(draft.name);
  if (!name) throw new Error('Task type name is required');
  const existing = await db.taskTypes.where('name').equals(name).first();
  if (existing) throw new Error('Task type already exists');

  const taskType: TaskType = {
    id: newId('tt'),
    name,
    description: trimmed(draft.description),
    isDefault: false,
    isSystem: false,
    createdAt: nowIso(),
  };
  await db.taskTypes.add(taskType);
  emitStoreChange('tasks');
  return taskType;
}

/** Removes a manager-defined type. Shipped types and types in use are kept. */
export async function deleteTaskType(actor: User, taskTypeId: string): Promise<boolean> {
  assertPermission(actor, 'DELETE_TASK');
  const taskType = await getTaskType(taskTypeId);
  if (!taskType) return false;
  if (taskType.isSystem) throw new Error('System task types cannot be deleted');
  if ((await db.tasks.where('taskTypeId').equals(taskTypeId).count()) > 0) {
    throw new Error('Task type is still in use');
  }
  await db.taskTypes.delete(taskTypeId);
  emitStoreChange('tasks');
  return true;
}

// ── Contacts ─────────────────────────────────────────────────────────

export async function getContacts(): Promise<Contact[]> {
  const contacts = await db.contacts.toArray();
  return contacts.sort((a, b) => a.fullName.localeCompare(b.fullName, 'he'));
}

export async function getContact(contactId: string): Promise<Contact | null> {
  return (await db.contacts.get(contactId)) ?? null;
}

/**
 * Files a contact for future use — the "שמור איש קשר זה לשימוש עתידי" toggle.
 * Passing an `id` updates that record instead of creating a second one.
 */
export async function saveContact(actor: User, draft: ContactDraft): Promise<Contact> {
  assertPermission(actor, 'CREATE_TASK');
  const fullName = trimmed(draft.fullName);
  const primaryPhone = trimmed(draft.primaryPhone);
  if (!fullName) throw new Error('Contact name is required');
  if (!primaryPhone) throw new Error('Contact phone is required');

  const existing = draft.id ? await getContact(draft.id) : null;
  const contact: Contact = {
    id: existing?.id ?? draft.id ?? newId('cnt'),
    fullName,
    role: trimmed(draft.role),
    organization: trimmed(draft.organization),
    primaryPhone,
    secondaryPhone: optional(draft.secondaryPhone),
    notes: optional(draft.notes),
    createdAt: existing?.createdAt ?? nowIso(),
  };
  await db.contacts.put(contact);
  emitStoreChange('tasks');
  return contact;
}

// ── Tasks ────────────────────────────────────────────────────────────

export interface TaskQuery {
  date?: string;
  statusId?: TaskStatusId;
  taskTypeId?: string;
}

/**
 * The board, chronologically. Narrowing by day/status/type is optional, and a
 * task in the recycle bin is never part of the answer — the bin is read through
 * {@link getDeletedTasks} alone.
 */
export async function getTasks(query: TaskQuery = {}): Promise<Task[]> {
  const tasks = (await db.tasks.toArray()).map(asCurrent);
  const matching = tasks.filter((task) => {
    if (isDeleted(task)) return false;
    if (query.date && task.date !== query.date) return false;
    if (query.statusId && task.statusId !== normalizeTaskStatus(query.statusId)) return false;
    if (query.taskTypeId && task.taskTypeId !== query.taskTypeId) return false;
    return true;
  });
  return sortTasks(matching);
}

/**
 * One task off the board. A deleted task reads as missing, so nothing that
 * edits, closes or reports on a task can reach one sitting in the bin.
 */
export async function getTask(taskId: string): Promise<Task | null> {
  const task = await getTaskRecord(taskId);
  return task && isDeleted(task) ? null : task;
}

/** The stored row whatever its state — the recycle bin's own reader. */
async function getTaskRecord(taskId: string): Promise<Task | null> {
  const task = await db.tasks.get(taskId);
  return task ? asCurrent(task) : null;
}

/** A contact typed into the form, cleaned up — or nothing worth storing. */
function normalizeManualContact(
  manualContact: ManualContact | undefined,
): ManualContact | undefined {
  if (!manualContact) return undefined;
  const name = trimmed(manualContact.name);
  const phone = trimmed(manualContact.phone);
  if (!name && !phone) return undefined;
  return { name, phone, role: optional(manualContact.role) };
}

/** Shared validation for both write paths. */
function validate(fields: Partial<TaskDraft>): void {
  if (fields.title !== undefined && !trimmed(fields.title)) {
    throw new Error('Task title is required');
  }
  if (fields.date !== undefined && !DATE_KEY.test(fields.date)) {
    throw new Error('Task date must be YYYY-MM-DD');
  }
  if (fields.startTime !== undefined && !TIME_OF_DAY.test(fields.startTime)) {
    throw new Error('Task start time must be HH:MM');
  }
  if (fields.estEndTime !== undefined && fields.estEndTime && !TIME_OF_DAY.test(fields.estEndTime)) {
    throw new Error('Task end time must be HH:MM');
  }
}

export async function createTask(actor: User, input: TaskInput): Promise<Task> {
  assertPermission(actor, 'CREATE_TASK');
  validate(input);
  if (!(await getTaskType(input.taskTypeId))) throw new Error('Unknown task type');

  // A saved contact wins over a typed one, so a task never carries both.
  const contactId = optional(input.contactId);
  const manualContact = contactId ? undefined : normalizeManualContact(input.manualContact);

  const stamp = nowIso();
  const task: Task = {
    id: newId('tsk'),
    title: trimmed(input.title),
    taskTypeId: input.taskTypeId,
    description: trimmed(input.description),
    date: input.date,
    startTime: input.startTime,
    estEndTime: trimmed(input.estEndTime),
    priority: input.priority ?? DEFAULT_TASK_PRIORITY,
    contactId,
    manualContact,
    destinationAddress: optional(input.destinationAddress),
    destinationLink: optional(input.destinationLink),
    // Every task opens בביצוע — there is no waiting room to file it in, and a
    // crew is bound to it afterwards without the status having to move.
    statusId: normalizeTaskStatus(input.statusId ?? DEFAULT_TASK_STATUS_ID),
    operationalNotes: optional(input.operationalNotes),
    internalNotes: optional(input.internalNotes),
    createdBy: actor.id,
    createdAt: stamp,
    updatedAt: stamp,
    // The name beside the id: the board reads "נוצר ע״י …" off the card without
    // having to resolve an account against the directory.
    ...stampCreated(actor),
  };
  await db.tasks.add(task);
  // Step one of the feed — a task that was never opened has no timeline.
  await logTaskTimelineEvent(actor, task.id, 'created', {
    description: task.title,
    locationText: task.destinationAddress,
    metadata: { priority: task.priority, date: task.date, startTime: task.startTime },
  });
  emitStoreChange('tasks');
  return task;
}

export async function updateTask(
  actor: User,
  taskId: string,
  patch: Partial<TaskDraft>,
): Promise<Task | null> {
  assertPermission(actor, 'EDIT_TASK');
  validate(patch);
  const task = await getTask(taskId);
  if (!task) return null;
  if (patch.taskTypeId !== undefined && !(await getTaskType(patch.taskTypeId))) {
    throw new Error('Unknown task type');
  }

  const merged: Task = {
    ...task,
    ...patch,
    statusId: normalizeTaskStatus(patch.statusId ?? task.statusId),
    updatedAt: nowIso(),
    ...stampUpdated(actor),
  };
  if (patch.contactId !== undefined || patch.manualContact !== undefined) {
    merged.contactId = optional(merged.contactId);
    merged.manualContact = merged.contactId
      ? undefined
      : normalizeManualContact(merged.manualContact);
  }
  if (patch.title !== undefined) merged.title = trimmed(patch.title);
  if (patch.description !== undefined) merged.description = trimmed(patch.description);
  if (patch.destinationAddress !== undefined) {
    merged.destinationAddress = optional(patch.destinationAddress);
  }
  if (patch.destinationLink !== undefined) merged.destinationLink = optional(patch.destinationLink);
  if (patch.operationalNotes !== undefined) merged.operationalNotes = optional(patch.operationalNotes);
  if (patch.internalNotes !== undefined) merged.internalNotes = optional(patch.internalNotes);

  await db.tasks.put(merged);
  // A cancellation is a lifecycle step, not an edit — the feed has to say so.
  if (merged.statusId === 'cancelled' && task.statusId !== 'cancelled') {
    await logTaskTimelineEvent(actor, taskId, 'cancelled', {
      description: merged.closingNote,
      metadata: { previousStatusId: task.statusId },
    });
  }
  emitStoreChange('tasks');
  return merged;
}

// ── Deletion & the recycle bin (סל מחזור) ────────────────────────────

/**
 * Moves a task to the recycle bin. Nothing is removed: the row is stamped with
 * `deletedAt`, which takes it off every board read, and it stays restorable for
 * {@link RECYCLE_BIN_RETENTION_DAYS} days.
 *
 * The vehicle teams, the field reports and the feed deliberately stay put — a
 * restored task has to come back whole. They are removed by {@link purgeTask},
 * which is the only place a task actually stops existing.
 */
export async function deleteTask(actor: User, taskId: string): Promise<boolean> {
  assertPermission(actor, 'DELETE_TASK');
  const task = await getTask(taskId);
  if (!task) return false;

  const stamp = nowIso();
  await db.tasks.put({
    ...task,
    deletedAt: stamp,
    deletedBy: actor.id,
    updatedAt: stamp,
    ...stampUpdated(actor),
  });
  await recordTaskEvent(actor, 'TASK_DELETED', taskId, `המשימה "${task.title}" הועברה לסל המחזור`, {
    statusId: task.statusId,
  });
  // A binned task releases its vehicle and its crew, so the rosters move too.
  emitStoreChange('tasks');
  return true;
}

/** The recycle bin itself — deleted tasks, most recently deleted first. */
export async function getDeletedTasks(): Promise<Task[]> {
  const rows = (await db.tasks.toArray()).map(asCurrent);
  return deletedTasks(rows);
}

/** "שחזר משימה" — clears the stamp and puts the task back on the board. */
export async function restoreTask(actor: User, taskId: string): Promise<Task | null> {
  assertPermission(actor, 'DELETE_TASK');
  const task = await getTaskRecord(taskId);
  if (!task || !isDeleted(task)) return null;

  const { deletedAt: _deletedAt, deletedBy: _deletedBy, ...restored } = task;
  const merged: Task = { ...restored, updatedAt: nowIso(), ...stampUpdated(actor) };
  await db.tasks.put(merged);
  await recordTaskEvent(actor, 'TASK_RESTORED', taskId, `המשימה "${task.title}" שוחזרה מסל המחזור`);
  emitStoreChange('tasks');
  return merged;
}

/**
 * Removes a task for good, together with everything that only existed for it:
 * its vehicle teams and crew, the field reports and the operational feed.
 * Reachable only for a task that is already in the bin.
 */
export async function purgeTask(actor: User, taskId: string): Promise<boolean> {
  assertPermission(actor, 'DELETE_TASK');
  const task = await getTaskRecord(taskId);
  if (!task || !isDeleted(task)) return false;
  await purgeTaskRow(task);
  await recordTaskEvent(actor, 'TASK_PURGED', taskId, `המשימה "${task.title}" נמחקה לצמיתות`);
  emitStoreChange('tasks');
  return true;
}

/** "רוקן סל מחזור" — purges every task in the bin. Returns how many went. */
export async function emptyRecycleBin(actor: User): Promise<number> {
  assertPermission(actor, 'DELETE_TASK');
  const bin = await getDeletedTasks();
  for (const task of bin) {
    await purgeTaskRow(task);
    await recordTaskEvent(actor, 'TASK_PURGED', task.id, `המשימה "${task.title}" נמחקה לצמיתות`);
  }
  if (bin.length > 0) emitStoreChange('tasks');
  return bin.length;
}

/**
 * The automatic sweep: every task that has been in the bin longer than the
 * retention window is removed for good. It is run whenever the board loads, so
 * an installation that is opened after a month of silence still tidies itself.
 *
 * No permission is asked for — the retention window is the system's own rule,
 * not somebody's action, so nothing is written to the ledger either.
 */
export async function purgeExpiredTasks(now: number = Date.now()): Promise<number> {
  const due = expiredTasks(await db.tasks.toArray(), now);
  for (const task of due) await purgeTaskRow(task);
  if (due.length > 0) emitStoreChange('tasks');
  return due.length;
}

/** The removal itself, shared by the manual and the automatic paths. */
async function purgeTaskRow(task: Task): Promise<void> {
  // The vehicle teams go with it — a crew row never outlives its task.
  await deleteTaskTeams(task.id);
  await db.taskProgress.where('taskId').equals(task.id).delete();
  await deleteTaskTimeline(task.id);
  await db.tasks.delete(task.id);
}

// ── Field execution (Task 8) ─────────────────────────────────────────

/**
 * The lifecycle statuses a soldier may move a task to from the field. Under the
 * two-state lifecycle that leaves one: בביצוע. Finishing and cancelling are not
 * on the list and never were — a soldier reports their own execution as
 * finished, and only a manager closes the task itself.
 */
export const SOLDIER_SETTABLE_STATUSES: readonly TaskStatusId[] = [ACTIVE_TASK_STATUS_ID];

/**
 * The timeline colour a field report may carry. A one-touch step from
 * `QuickStatusUpdateBar` names its own lifecycle step; a typed update carries
 * none and is filed as movement.
 */
export interface FieldUpdateOptions {
  eventType?: TimelineEventType;
  locationText?: string;
}

/** What a hand-written timeline note may carry beyond its text. */
export interface TimelineNoteOptions extends FieldUpdateOptions {
  /** Managers may pin a note to one vehicle team; a soldier's is pinned to theirs. */
  teamId?: string;
}

/** Every field report filed against one task, one row per soldier. */
export async function getTaskProgress(taskId: string): Promise<SoldierTaskProgress[]> {
  if (!taskId) return [];
  const rows = await db.taskProgress.where('taskId').equals(taskId).toArray();
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * The field reports of several tasks at once — what the command dashboard reads
 * to tell a task the crew has finished from one still running.
 */
export async function getProgressForTasks(
  taskIds: readonly string[],
): Promise<SoldierTaskProgress[]> {
  const ids = [...new Set(taskIds.filter(Boolean))];
  if (ids.length === 0) return [];
  const rows = await db.taskProgress.where('taskId').anyOf(ids).toArray();
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** One soldier's own row on one task, or `null` before they reported anything. */
export async function getSoldierTaskProgress(
  taskId: string,
  soldierId: string,
): Promise<SoldierTaskProgress | null> {
  if (!taskId || !soldierId) return null;
  return (await db.taskProgress.get(soldierProgressId(taskId, soldierId))) ?? null;
}

/**
 * The one guard every field report passes.
 *
 * A soldier may only report for themselves, and only on a task their own
 * vehicle team rides for — both answers come from `resolveTaskView()`, so the
 * store and the screens enforce exactly the same boundary. A manager reporting
 * on a crew member's behalf still needs `UPDATE_TASK_STATUS`, and the soldier
 * still has to be on the crew.
 */
async function assertFieldReport(
  actor: User,
  taskId: string,
  soldierId: string,
): Promise<{ task: Task; teamId?: string }> {
  assertPermission(actor, 'UPDATE_TASK_STATUS');
  if (!soldierId) throw new Error('Soldier id is required');
  const task = await getTask(taskId);
  if (!task) throw new Error('Unknown task');

  const [teams, soldiers] = await Promise.all([getTaskTeams(taskId), db.soldiers.toArray()]);
  if (!assignedSoldierIdsOf(teams).includes(soldierId)) {
    throw new Error('Soldier is not assigned to this task');
  }

  if (actor.role === 'soldier') {
    const view = resolveTaskView(actor, { teams, soldiers });
    if (view.mode !== 'soldier' || !view.canReportFromField) {
      throw new Error('אינך משויך למשימה זו');
    }
    if (view.soldierId !== soldierId) {
      throw new Error('A soldier may only report for themselves');
    }
  }

  // The vehicle the report belongs to, so the feed can be read one crew at a
  // time — the crew boundary the router already draws on the screens.
  return { task, teamId: soldierTeamOf(teams, soldierId)?.id };
}

/** Creates the soldier's row on first report, then patches it. */
async function patchProgress(
  taskId: string,
  soldierId: string,
  userId: string | undefined,
  patch: Partial<Omit<SoldierTaskProgress, 'id' | 'taskId' | 'soldierId' | 'createdAt'>>,
): Promise<SoldierTaskProgress> {
  const id = soldierProgressId(taskId, soldierId);
  const existing = await db.taskProgress.get(id);
  const stamp = nowIso();
  const row: SoldierTaskProgress = {
    id,
    taskId,
    soldierId,
    userId: existing?.userId ?? userId,
    receiptConfirmedAt: existing?.receiptConfirmedAt,
    finishedAt: existing?.finishedAt,
    finishNote: existing?.finishNote,
    updates: existing?.updates ?? [],
    ...patch,
    createdAt: existing?.createdAt ?? stamp,
    updatedAt: stamp,
  };
  await db.taskProgress.put(row);
  return row;
}

/**
 * "אישור קבלת משימה" — the soldier acknowledges the task in one tap. It is
 * idempotent: a second tap neither moves the timestamp nor files a second row
 * in the ledger.
 */
export async function confirmTaskReceipt(
  actor: User,
  taskId: string,
  soldierId: string,
): Promise<SoldierTaskProgress> {
  const { teamId } = await assertFieldReport(actor, taskId, soldierId);
  const existing = await getSoldierTaskProgress(taskId, soldierId);
  if (existing?.receiptConfirmedAt) return existing;

  const row = await patchProgress(taskId, soldierId, actor.id, {
    receiptConfirmedAt: nowIso(),
  });
  await recordTaskEvent(actor, 'TASK_RECEIPT_CONFIRMED', taskId, 'החייל אישר קבלת המשימה', {
    soldierId,
  });
  await logTaskTimelineEvent(actor, taskId, 'receipt_confirmed', {
    teamId,
    metadata: { soldierId },
  });
  emitStoreChange('tasks');
  return row;
}

/**
 * "עדכן סטטוס / הוסף הערה" — a line from the field, optionally moving the task
 * to one of {@link SOLDIER_SETTABLE_STATUSES}. Reporting from the field also
 * counts as receiving the task, so the first update confirms the receipt too.
 */
export async function addSoldierTaskUpdate(
  actor: User,
  taskId: string,
  soldierId: string,
  note: string,
  statusId?: TaskStatusId,
  options: FieldUpdateOptions = {},
): Promise<SoldierTaskProgress> {
  const { task, teamId } = await assertFieldReport(actor, taskId, soldierId);
  const text = trimmed(note);
  if (!text && !statusId) throw new Error('An update needs a note or a status');
  if (statusId && !SOLDIER_SETTABLE_STATUSES.includes(statusId)) {
    throw new Error('A soldier may not move the task to this status');
  }

  const existing = await getSoldierTaskProgress(taskId, soldierId);
  const update: SoldierTaskUpdate = {
    id: newId('upd'),
    note: text,
    statusId,
    createdAt: nowIso(),
  };
  const row = await patchProgress(taskId, soldierId, actor.id, {
    receiptConfirmedAt: existing?.receiptConfirmedAt ?? nowIso(),
    updates: [...(existing?.updates ?? []), update],
  });

  if (statusId && task.statusId !== statusId) {
    await db.tasks.put({ ...task, statusId, updatedAt: nowIso() });
  }

  await recordTaskEvent(
    actor,
    'SOLDIER_TASK_UPDATE',
    taskId,
    text ? `עדכון מהשטח — ${text}` : 'עדכון סטטוס מהשטח',
    { soldierId, statusId },
  );
  // A one-touch step names itself ("הגעתי ליעד"); a free-text line from the
  // field is filed as movement, with the soldier's own words underneath.
  await logTaskTimelineEvent(actor, taskId, options.eventType ?? 'in_transit', {
    teamId,
    statusTitle: options.eventType ? undefined : 'עדכון מהשטח',
    description: text,
    locationText: options.locationText,
    metadata: { soldierId, statusId, updateId: update.id },
  });
  emitStoreChange('tasks');
  return row;
}

/**
 * "סימון סיום ביצוע" — the soldier's own finish, with an optional note. It
 * never completes the task: the board still shows it open until a manager
 * closes it through {@link closeTaskAsManager}.
 */
export async function markSoldierFinished(
  actor: User,
  taskId: string,
  soldierId: string,
  note?: string,
): Promise<SoldierTaskProgress> {
  const { teamId } = await assertFieldReport(actor, taskId, soldierId);
  const existing = await getSoldierTaskProgress(taskId, soldierId);
  const finishNote = optional(note);
  const row = await patchProgress(taskId, soldierId, actor.id, {
    receiptConfirmedAt: existing?.receiptConfirmedAt ?? nowIso(),
    finishedAt: existing?.finishedAt ?? nowIso(),
    finishNote: finishNote ?? existing?.finishNote,
  });
  if (existing?.finishedAt) return row;

  await recordTaskEvent(
    actor,
    'SOLDIER_FINISHED',
    taskId,
    finishNote ? `החייל סימן סיום ביצוע — ${finishNote}` : 'החייל סימן סיום ביצוע',
    { soldierId },
  );
  await logTaskTimelineEvent(actor, taskId, 'soldier_completed', {
    teamId,
    description: finishNote,
    metadata: { soldierId },
  });
  emitStoreChange('tasks');
  return row;
}

/**
 * The manager's closure — the other half of the separation. Needs `EDIT_TASK`,
 * moves the task to `completed` and stamps who closed it and when.
 */
export async function closeTaskAsManager(
  actor: User,
  taskId: string,
  note?: string,
): Promise<Task | null> {
  assertPermission(actor, 'EDIT_TASK');
  const task = await getTask(taskId);
  if (!task) return null;

  const closed: Task = {
    ...task,
    statusId: COMPLETED_TASK_STATUS_ID,
    closedAt: nowIso(),
    closedBy: actor.id,
    closingNote: optional(note) ?? task.closingNote,
    updatedAt: nowIso(),
    ...stampUpdated(actor),
  };
  await db.tasks.put(closed);
  await recordTaskEvent(
    actor,
    'TASK_CLOSED',
    taskId,
    closed.closingNote ? `המשימה נסגרה — ${closed.closingNote}` : 'המשימה נסגרה על ידי מנהל',
    { statusId: closed.statusId },
  );
  // The last node of the feed. A soldier's own finish never writes this one.
  await logTaskTimelineEvent(actor, taskId, 'manager_closed', {
    description: closed.closingNote,
    metadata: { statusId: closed.statusId, closedBy: actor.id },
  });
  // Closing the task hands its vehicle and its crew back to the pool, so the
  // rosters and the readiness counters change on this write, not on a reload.
  emitStoreChange('tasks');
  return closed;
}

// ── Custom timeline notes (Task 9) ───────────────────────────────────

/**
 * A note attached to the task's feed by hand — an operational line that is not
 * one of the reported steps ("התעכבנו במחסום", "הלקוח ביקש לדחות בשעה").
 *
 * Who may write one is the router's answer, not a role test taken here: a
 * manager writes on any task they see, a soldier writes only on the task their
 * own vehicle rides for, and only while they may report from the field. The
 * predicate itself lives in `canAddTimelineNote()` so the screens and the
 * database read the same boundary.
 */
export async function addTimelineNote(
  actor: User,
  taskId: string,
  note: string,
  options: TimelineNoteOptions = {},
): Promise<TimelineEvent> {
  assertPermission(actor, 'UPDATE_TASK_STATUS');
  const text = trimmed(note);
  if (!text) throw new Error('A timeline note needs text');

  const task = await getTask(taskId);
  if (!task) throw new Error('Unknown task');

  const [teams, soldiers] = await Promise.all([getTaskTeams(taskId), db.soldiers.toArray()]);
  const view = resolveTaskView(actor, { teams, soldiers });
  if (!canAddTimelineNote(actor, view)) throw new PermissionDeniedError('UPDATE_TASK_STATUS');

  // A soldier's note is filed against their own vehicle, never the convoy.
  const teamId =
    view.mode === 'soldier'
      ? view.team?.id
      : optional(options.teamId);

  const event = await logTaskTimelineEvent(actor, taskId, options.eventType ?? 'in_transit', {
    teamId,
    statusTitle: options.eventType ? undefined : 'הערה מהשטח',
    description: text,
    locationText: options.locationText,
    metadata: { note: true, viewMode: view.mode },
  });
  emitStoreChange('timeline');
  return event;
}

// ── Maintenance ──────────────────────────────────────────────────────

/**
 * Wipes the task tables — used by the "reset local data" action. The assignment
 * tables, the operational feed and the quick mission log go with them: a vehicle
 * team and a timeline event only exist for a task, and a logged journey is
 * evidence about a board that no longer exists.
 */
export async function resetTaskData(): Promise<void> {
  await Promise.all([
    db.tasks.clear(),
    db.taskTypes.clear(),
    db.contacts.clear(),
    db.taskProgress.clear(),
  ]);
  await resetAssignmentData();
  await resetTimelineData();
  await resetQuickDriverTasks();
  emitStoreChange('tasks');
}
